import type { GridColDef } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type { DecisionBehaviorOfSubitems } from "../../../../clients/graphqlTypes.js";
import { DataGridDecisionBehaviorCell } from "../cells/DataGridDecisionBehaviorCell.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import type { SetDecisionBehaviorFn } from "../useDecisionBehaviorToggle.js";
import type { DocDataGridRow, PendingCellEdit } from "../types.js";

interface Options {
  headerName: string;
  canEdit: boolean;
  setDecisionBehavior: SetDecisionBehaviorFn;
}

function DecisionBehaviorLabel({
  value,
}: {
  value: DecisionBehaviorOfSubitems | undefined;
}) {
  const { t } = useTranslate("Decisions");
  if (!value) return null;
  const labels: Record<DecisionBehaviorOfSubitems, string> = {
    NONE: t("NONE", { ns: "Decisions" }),
    SELECT_MANY: t("SELECT_MANY", { ns: "Decisions" }),
    SELECT_ONE: t("SELECT_ONE", { ns: "Decisions" }),
  };
  return <span>{labels[value]}</span>;
}

export function createDecisionBehaviorColumn<
  T extends DocDataGrid_ItemFragment,
>(options: Options): GridColDef<DocDataGridRow<T>> {
  return {
    field: "decisionBehavior",
    headerName: options.headerName,
    width: 120,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    valueGetter: (_value, row) =>
      row.item?.decisionBehaviorOfSubitems ?? undefined,
    valueSetter: (value: DecisionBehaviorOfSubitems, row) => ({
      ...row,
      item: row.item
        ? { ...row.item, decisionBehaviorOfSubitems: value }
        : null,
      _pendingEdit: {
        apply: (itemId: string) => options.setDecisionBehavior(itemId, value),
      },
    }),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      if (params.row.item.type !== "decision") return null;
      return (
        <DecisionBehaviorLabel
          value={params.value as DecisionBehaviorOfSubitems | undefined}
        />
      );
    },
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      if (params.row.item.type !== "decision") return null;
      return <DataGridDecisionBehaviorCell {...params} />;
    },
  };
}
